import {
  Drawer,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../utils/redux';
import FunctionBuilder from './FunctionBuilder';
import TestResponse from './TestResponse';
import {
  createWebhookFunctionApi,
  updateWebhookFunctionApi,
  testWebhookFunctionApi,
} from '../../../utils/apis';
import { ICreateFunction } from '../../../schemas/apis/interface';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { resetFunctionBuilder } from '../../../utils/redux/slices/webhookFunctionSlice';
import { useDashboard } from '../../../hooks/useDashboard';

const WebHookFunctionDrawer = ({
  open,
  onClose,
  isEditing = false,
  webhookData = null,
}: {
  open: boolean;
  onClose: () => void;
  isEditing?: boolean;
  webhookData?: any;
}) => {
  const [activeTab, setActiveTab] = useState('function-builder');
  const [testResponse, setTestResponse] = useState<any>(null);

  const webhookFunctionState = useSelector(
    (state: RootState) => state.webhookFunction
  );
  const dispatch = useDispatch();
  const { selectedAssistant } = useDashboard();
  const queryClient = useQueryClient();

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: 'function-builder' | 'test-response'
  ) => {
    setActiveTab(newValue);
  };

  const { mutate: createFunction, isPending: isCreatingFunctionPending } =
    useMutation({
      mutationFn: createWebhookFunctionApi,
      onSuccess: () => {
        onClose();
        dispatch(resetFunctionBuilder());
        queryClient.invalidateQueries({ queryKey: ['agents'] });
      },
      onError: (error: any) => {
        console.log(error);
      },
    });

  const { mutate: updateFunction, isPending: isUpdatingFunctionPending } =
    useMutation({
      mutationFn: (data: {
        agentId: string;
        functionName: string;
        updateData: Partial<ICreateFunction>;
      }) =>
        updateWebhookFunctionApi(
          data.agentId,
          data.functionName,
          data.updateData
        ),
      onSuccess: () => {
        onClose();
        dispatch(resetFunctionBuilder());
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        if (selectedAssistant?._id) {
          queryClient.refetchQueries({
            queryKey: ['agent', selectedAssistant._id],
          });
        }
      },
      onError: (error: any) => {
        console.log(error);
      },
    });

  const { mutate: testFunction, isPending: isTestingFunctionPending } =
    useMutation({
      mutationFn: (data: {
        agentId: string;
        functionName: string;
        parameters: Record<string, any>;
      }) =>
        testWebhookFunctionApi(
          data.agentId,
          data.functionName,
          data.parameters
        ),
      onSuccess: data => {
        setTestResponse(data.data);
      },
      onError: (error: any) => {
        console.error('Error testing function:', error);
        setTestResponse({
          error: 'Failed to test function. See console for details.',
        });
      },
    });

  const handleTestFunction = () => {
    try {
      setActiveTab('test-response');

      // Create parameters object from parameter mappings
      const testParameters: Record<string, any> = {};
      webhookFunctionState.parameterMappings.forEach(mapping => {
        if (mapping.functionParameter) {
          testParameters[mapping.functionParameter] = mapping.value || '';
        }
      });

      // Call the test API using the mutation
      testFunction({
        agentId: selectedAssistant?._id || '',
        functionName: webhookFunctionState.functionName,
        parameters: testParameters,
      });
    } catch (error) {
      console.error('Error in handleTestFunction:', error);
    }
  };

  const handleSaveFunction = () => {
    // Build required parameters array
    const requiredParams = webhookFunctionState.parameters
      .filter(param => param.required)
      .map(param => param.name);

    // Transform parameters to required format
    const propertiesObj: Record<string, any> = {};

    webhookFunctionState.parameters.forEach(param => {
      // Check if parameter is an array type with nested objects
      if (param.type === 'array' && param.arrayItems) {
        propertiesObj[param.name] = {
          type: 'array',
          description: param.description,
        };

        // Use custom item name instead of "items"
        const customItemName = param.arrayItems.itemsName || 'items';

        // Create custom item property instead of standard "items"
        propertiesObj[param.name][customItemName] = {
          type: 'object',
          properties: {},
          required: [],
        };

        // Add properties to custom item name
        if (param.arrayItems.properties) {
          Object.entries(param.arrayItems.properties).forEach(
            ([propName, propDetails]) => {
              propertiesObj[param.name][customItemName].properties[propName] = {
                type: propDetails.type,
                description: propDetails.description || '',
              };

              // Add required properties
              if (
                param?.arrayItems?.required &&
                param?.arrayItems?.required.includes(propName)
              ) {
                propertiesObj[param.name][customItemName].required.push(
                  propName
                );
              }
            }
          );
        }
      } else {
        // Handle regular non-array parameters
        propertiesObj[param.name] = {
          type: param.type,
          description: param.description,
        };
      }
    });

    // Transform parameter mappings
    const parameterMappings = webhookFunctionState.parameterMappings.map(
      mapping => ({
        source: mapping.functionParameter || '',
        target: mapping.apiParameter || '',
        location: mapping.location || 'params',
        value: mapping.value || '',
      })
    );

    // Transform result mappings
    const resultMapping: Record<string, string> = {};
    webhookFunctionState.resultMappings.forEach(mapping => {
      resultMapping[mapping.resultField] = mapping.apiResponsePath;
    });

    // Build fixed params from query params
    const fixedParams: Record<string, string> = {};
    webhookFunctionState.apiConfig.queryParams.forEach(param => {
      fixedParams[param.key] = param.value;
    });

    const functionData: ICreateFunction = {
      agentId: selectedAssistant?._id || '',
      name: webhookFunctionState.functionName,
      description: webhookFunctionState.functionDescription,
      function_type: 'webhook',
      parameters: {
        type: 'object',
        properties: propertiesObj,
        required: requiredParams,
      },
      api_config: {
        endpoint: webhookFunctionState.apiConfig.endpoint,
        method: webhookFunctionState.apiConfig.method,
        fixed_params: fixedParams,
        parameter_mappings: parameterMappings,
        result_mapping: resultMapping,
        processing_message: webhookFunctionState.apiConfig.processingMessage,
      },
    };

    if (isEditing && webhookData) {
      // For update, compare with original data and only send changed fields
      const updatedFields: Partial<ICreateFunction> = {};

      // Add basic fields that might have changed
      if (webhookFunctionState.functionName !== webhookData.name) {
        updatedFields.name = webhookFunctionState.functionName;
      }

      if (
        webhookFunctionState.functionDescription !== webhookData.description
      ) {
        updatedFields.description = webhookFunctionState.functionDescription;
      }

      // Compare and add API config if changed
      const originalApiConfig = webhookData.api_config || {};
      const newApiConfig = functionData.api_config;

      const apiConfigChanged =
        originalApiConfig.endpoint !== newApiConfig.endpoint ||
        originalApiConfig.method !== newApiConfig.method ||
        JSON.stringify(originalApiConfig.fixed_params) !==
          JSON.stringify(newApiConfig.fixed_params) ||
        JSON.stringify(originalApiConfig.parameter_mappings) !==
          JSON.stringify(newApiConfig.parameter_mappings) ||
        JSON.stringify(originalApiConfig.result_mapping) !==
          JSON.stringify(newApiConfig.result_mapping) ||
        originalApiConfig.processing_message !==
          newApiConfig.processing_message;

      if (apiConfigChanged) {
        updatedFields.api_config = newApiConfig;
      }

      // Compare and add parameters if changed
      if (
        JSON.stringify(webhookData.parameters) !==
        JSON.stringify(functionData.parameters)
      ) {
        updatedFields.parameters = functionData.parameters;
      }

      // Always include agentId
      updatedFields.agentId = selectedAssistant?._id || '';

      // Update with only changed fields
      updateFunction({
        agentId: selectedAssistant?._id || '',
        functionName: webhookData.name,
        updateData: updatedFields,
      });
    } else {
      createFunction(functionData);
    }
  };

  const isPending =
    isCreatingFunctionPending ||
    isUpdatingFunctionPending ||
    isTestingFunctionPending;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close (Cancel) Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <X size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              {isEditing ? 'Edit' : 'Create'} Webhook Function
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Test Button */}
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleTestFunction}
              disabled={
                isPending ||
                isTestingFunctionPending ||
                !webhookFunctionState.functionName
              }
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              {isTestingFunctionPending ? 'Testing...' : 'Test Function'}
            </Button>

            {/* Save Button */}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSaveFunction}
              disabled={isPending || !webhookFunctionState.functionName}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              {isUpdatingFunctionPending || isCreatingFunctionPending
                ? isEditing
                  ? 'Updating...'
                  : 'Saving...'
                : isEditing
                  ? 'Update Function'
                  : 'Save Function'}
            </Button>
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -2,
          }}
        />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mx: -2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="webhook function tabs"
            sx={{
              minHeight: 'unset',
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 'unset',
                py: 1,
                px: 2,
                mx: 0.5,
              },
            }}
          >
            <Tab label="Function Builder" value="function-builder" />
            <Tab label="Test Response" value="test-response" />
          </Tabs>
        </Box>
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Function Builder */}
        {activeTab === 'function-builder' && <FunctionBuilder />}

        {/* Test Response */}
        {activeTab === 'test-response' && (
          <TestResponse
            testData={testResponse}
            isLoading={isTestingFunctionPending}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default WebHookFunctionDrawer;
